import React, { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { useUniqueId } from 'src/hooks'
import { Format, If, Unless, Button, HeightFade } from 'src/Components'
import {
  Heading,
  ProConList,
  Pro,
  Info,
  Strong
} from 'src/Components/shared/Gem/GemDetail'
import { dreamHome } from 'src/constants/equity'
import MonthlyPayment from 'src/Components/homeowners/PurchasingPower/MonthlyPayment'
import BuyersLink from 'src/Components/homeowners/PurchasingPower/BuyersLink'
import CTAHeading from 'src/Components/homeowners/PurchasingPower/CTAHeading'
import FeeRange from 'src/Components/homeowners/PurchasingPower/FeeRange'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import CMASection from 'src/Components/shared/EstimatedValue/CMASection'
import { selectInstantOfferEnabled } from 'src/store/selectors/powerUps'
import {
  selectDreamHomeDownPayment,
  selectDreamHomeBudget,
  selectDreamHomeMonthlyPayment
} from 'src/store/selectors/home/equity/dreamHome'
import { selectDreamHomeMortgageRate } from 'src/store/selectors/mortgageRate'
import { selectShowBuyersLink } from 'src/store/selectors/layout'
import { redrawHeightFadeKey } from 'src/actions/transitions'
import { FROM, OVERLAY_HELPERS } from 'src/constants/navigation'
import APR from 'src/Components/shared/APR'
import history from 'src/history'

import styles from './DreamHome.module.scss'

const { DREAM_HOME } = FROM

const DreamHomeContent = () => {
  const dispatch = useDispatch()
  const heightFadeId = useUniqueId('DreamHome_HF_')
  const refCMA = useRef(null)
  const refIBuy = useRef(null)
  const [showCMA, setShowCMA] = useState(false)
  const [cmaStep, setCmaStep] = useState(null)
  const iBuyEnabled = useSelector(selectInstantOfferEnabled)

  const budget = useSelector(selectDreamHomeBudget)
  const downPayment = useSelector(selectDreamHomeDownPayment)
  const monthlyPayment = useSelector(selectDreamHomeMonthlyPayment)
  const mortgageRate = useSelector(selectDreamHomeMortgageRate)
  const showBuyersLink = useSelector(selectShowBuyersLink)

  return (
    <div>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.dreamHomeHeading'
          defaultMessage='Using {downPayment} of your estimated equity, you could make a {percent} down payment on a new house worth around {budget}!'
          values={{
            percent: (
              <Format.Percentage value={dreamHome.downPaymentPercent * 100} />
            ),
            downPayment: (
              <Strong>
                <Format.Currency abbreviate value={downPayment} />
              </Strong>
            ),
            budget: (
              <Strong>
                <Format.Currency abbreviate value={budget} />
              </Strong>
            )
          }}
        />
      </Heading>

      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeOne'
            defaultMessage='Your new monthly principal and interest payment on a {loanTerm}-year mortgage at {apr} would be around {payment}'
            values={{
              apr: <APR value={mortgageRate} />,
              loanTerm: dreamHome.loanTerm,
              payment: <MonthlyPayment value={monthlyPayment} />
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.dreamHomeTwo'
            defaultMessage='Get more square footage, a larger yard, or switch neighborhoods—you have options!'
          />
        </Pro>
        <Info>
          <FeeRange />
        </Info>
      </ProConList>

      <CTAHeading className={styles.cta}>
        <FormattedMessage
          id='Homeowner.purchasePower.ctaHeading'
          defaultMessage="So, what's next?"
        />
      </CTAHeading>

      <Button.Group className={styles.buttonWrapper}>
        <If condition={showBuyersLink}>
          <BuyersLink affordability={budget} from={DREAM_HOME}>
            <FormattedMessage
              id='Homeowner.purchasePower.explore'
              defaultMessage='Start your search'
            />
          </BuyersLink>
        </If>
        <Unless condition={iBuyEnabled}>
          <Button
            ref={refCMA}
            onClick={() => {
              history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())
            }}
          >
            <FormattedMessage
              id='Homeowner.purchasePower.dreamHomeButton.cma'
              defaultMessage='Verify selling price'
            />
          </Button>
        </Unless>
        <If condition={iBuyEnabled}>
          <Button
            ref={refIBuy}
            onClick={() => {
              history.push(OVERLAY_HELPERS.instantOffer.buildPath())
            }}
          >
            <FormattedMessage
              id='Homeowner.instantOffer.cta'
              defaultMessage='Instant Offer'
            />
          </Button>
        </If>
      </Button.Group>

      <AdditionalInfo
        visible={showCMA}
        cardWidth
        showDisclaimers={false}
        targetRef={showCMA ? refCMA.current : refIBuy.current}
        className={classnames(styles.bottomFlush, showCMA && styles.bottomOpen)}
      >
        <HeightFade
          id={heightFadeId}
          speed={cmaStep && showCMA ? 'fast' : 'medium'}
        >
          <If condition={showCMA}>
            <CMASection
              onStepChange={step => {
                setCmaStep(step)
                dispatch(redrawHeightFadeKey(heightFadeId, 'fast'))
              }}
              close={() => {
                setCmaStep(null)
                setShowCMA(false)
                dispatch(redrawHeightFadeKey(heightFadeId))
              }}
            />
          </If>
          <If condition={!showCMA}>
            <span />
          </If>
        </HeightFade>
      </AdditionalInfo>
    </div>
  )
}

export default DreamHomeContent
